/* global moment */
import { debounce } from '@shared/script/utils/debounce';

/**
 * Bind sticky header behavior to the planner,
 * returns a method to check if user is currently dragging.
 *
 * @param { HTMLElement } root
 * @returns { isDragging: function(): boolean }
 */
function bindStickyHeader(root) {
    const header = root.querySelector('.header');
    const buffer = root.querySelector('.header-buffer');

    if (!header || !buffer) {
        return;
    }

    let isDragging = false;

    let startX;
    let scrollLeft;

    header.addEventListener('mousedown', (e) => {
        isDragging = true;
        startX = e.pageX - header.offsetLeft;
        scrollLeft = header.scrollLeft;
    });

    ['mouseleave', 'mouseup'].forEach(evt =>
        header.addEventListener(evt, () => isDragging = false)
    );

    header.addEventListener('mousemove', (e) => {
        if (!isDragging) {
            return;
        }
        e.preventDefault();
        const x = e.pageX - header.offsetLeft;
        const walk = x - startX;
        header.scrollLeft = scrollLeft - walk;
    });

    window.addEventListener('scroll', () => {
        const shouldPin = window.scrollY > root.offsetTop + header.clientHeight * 2;
        header.classList.toggle('pinned', shouldPin);
        buffer.classList.toggle('pinned', shouldPin);
    });

    return {
        isDragging: () => isDragging
    };
}

/**
 * Resolve initial state from URL and data attributes.
 * Return data object to deconstruct initial view.
 *
 * @param { HTMLElement } root
 * @returns { Object}
 */
function resolveInitialState(root) {
    const url = new URL(window.location.href);
    const paramDays = url.searchParams.get('days');

    const customTime = url.searchParams.has('pep_custom_time')
        ? moment(url.searchParams.get('pep_custom_time'))
        : null;

    const now = customTime || moment();
    const startDate = root.dataset.startDate ? moment(root.dataset.startDate) : now;
    const endDate = root.dataset.endDate ? moment(root.dataset.endDate) : null;

    const hasDateRange = root.dataset.hasCustomDateRange === '1';

    return {
        url,
        paramDays,
        customTime,
        now,
        startDate,
        endDate,
        hasDateRange
    };
}

/**
 * Sets the value of the today button if it exists.
 *
 * @param { Object } now
 */
function setTodayButtonValue(now) {
    document.querySelector('.day-option.today')
        ?.setAttribute('data-value', now.format('YYYY-MM-DD'));
}

function appendDayFilter(date) {
    const li = document.createElement('li');
    const button = document.createElement('div');
    button.classList.add('btn', 'btn-default', 'day-option');
    button.setAttribute('role', 'radio');
    button.setAttribute('data-value', date);
    button.setAttribute('aria-selected', 'false');
    button.textContent = moment(date).format('ddd');
    li.appendChild(button);
    return li;
}

/**
 * Build our list of filters given a start and end date.
 *
 * @param { Object } startDate - moment start date object
 * @param { Object } endDate - moment end date object
 * @param { HTMLElement } root - root element of the planner
 * @returns { HTMLElement[], HTMLElement | null } - array of day filter buttons
 */
function renderDayFilters(startDate, endDate, root, hasDateRange = false) {

    const container = root.querySelector('.day-filters');
    if (!container) {
        return { dayFilters: [], allFilter: null };
    }

    const rendered = [];

    const allFilter = container.querySelector('[data-value="all"]');
    const todayButton = container.querySelector('.day-option.today');

    if (todayButton) {
        rendered.push(todayButton);
    }

    // Render filters
    if (!hasDateRange) {
        for (let i = 1; i < 7; i++) {
            const date = startDate.clone().add(i, 'days').format('YYYY-MM-DD');
            const li = appendDayFilter(date);
            container.appendChild(li);
            rendered.push(li.querySelector('.day-option'));
        }
    } else {
        const rangeDays = endDate.diff(startDate, 'days') + 1;
        const dayRange = Math.min(rangeDays, 7);

        for (let i = 0; i < dayRange; i++) {
            const date = startDate.clone().add(i, 'days').format('YYYY-MM-DD');
            const li = appendDayFilter(date);
            container.appendChild(li);
            rendered.push(li.querySelector('.day-option'));
        }
    }

    return { dayFilters: rendered, allFilter };
}

/**
 * Append or remove days from the URL query string.
 *
 * @param { Array } days - array of day strings (YYYY-MM-DD)
 * @param { URL } url - current URL object
 */
function updateDaysInUrl(days, url) {
    if (days.length > 0) {
        url.searchParams.set(
            'days',
            days.map(d => moment(d).locale('en').format('ddd').toLowerCase()).join(',')
        );
    } else {
        url.searchParams?.delete('days');
    }

    window.history.replaceState(null, '', url.toString());
}

/**
 * Toggle visibility of empty list message based on movie visibility.
 */
function toggleEmptyListVisibility() {
    const movies = document.querySelectorAll('.movie-card');

    const allHidden = Array.from(movies).every(m =>
        window.getComputedStyle(m).display === 'none'
    );

    document.querySelector('.movie-cards')?.style.setProperty('display', allHidden ? 'none' : 'grid');
    document.querySelector('.empty-list')?.style.setProperty('display', allHidden ? 'block' : 'none');
}

/**
 * Pre-select day filters based on `days` URL param (e.g. "mon,tue,wed").
 * Falls back to "all" if none matched.
 *
 * @param { string|null } paramDays - Comma-separated short day names from URL.
 * @param { HTMLElement[] } dayFilters - All filter button elements.
 * @param { HTMLElement|null } allFilter - The "all" button, if present.
 */
function applyPresetDayFilters(paramDays, dayFilters, allFilters) {
    if (!paramDays) {
        return;
    }

    const dayParams = paramDays.split(','); // ['mon', 'tue', 'wed']

    let matched = false;

    dayFilters.forEach(filter => {
        const day = filter.dataset.value;
        const shortDay = moment(day).locale('en').format('ddd').toLowerCase(); // e.g. "mon"

        if (dayParams.includes(shortDay)) {
            filter.setAttribute('aria-selected', 'true');
            matched = true;
        }
    });

    if (matched && allFilters) {
        allFilters.setAttribute('aria-selected', 'false');
    }
}

function getSelectedDays(dayFilters) {
    return dayFilters
        .filter(f => f.getAttribute('aria-selected') === 'true')
        .map(f => f.dataset.value)
        .filter(v => v !== 'all');
}

function bindFilterClickHandlers(dayFilters, allFilter, root, isDraggingFn, onUpdate) {
    const header = root.querySelector('.header');

    dayFilters.forEach(filter => {
        filter.addEventListener('click', () => {
            if (isDraggingFn()) {
                return;
            }

            if (header?.classList.contains('pinned')) {
                root.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }

            const isAll = filter.dataset.value === 'all';
            const wasSelected = filter.getAttribute('aria-selected') === 'true';

            if (isAll) {
                dayFilters.forEach(f => f.setAttribute('aria-selected', 'false'));
            } else {
                allFilter?.setAttribute('aria-selected', 'false');
            }

            filter.setAttribute('aria-selected', (!wasSelected).toString());

            const selected = getSelectedDays(dayFilters);
            if (selected.length === 0) {
                allFilter?.setAttribute('aria-selected', 'true');
            }

            onUpdate(selected);
        });
    });
}

/**
 * Update the separator row styling based on number of rows in the shows list.
 */
function updateSeparator(showsList, day) {
    const rowCount = getComputedStyle(showsList)
        .getPropertyValue('grid-template-rows')
        .split(' ')
        .length;

    const separator = day.querySelector('.separator');
    if (separator) {
        separator.classList.toggle('not-empty', rowCount > 1);
    }
}

/**
 * Decorate the movie cards depending on the UI state.
 */
function updateMovies() {
    const movies = document.querySelectorAll('.movie-card');

    movies.forEach(movie => {
        const dayElements = movie.querySelectorAll('.day');

        dayElements.forEach(day => {
            const showsList = day.querySelector('.shows');
            const noShowsPlaceholder = day.querySelector('.no-show-placeholder');

            if (!showsList) {
                return;
            }

            const showCount = showsList.children.length;

            // Show/hide empty state
            if (showCount === 0) {
                noShowsPlaceholder?.style.setProperty('display', 'block');
                showsList.style.setProperty('display', 'none');
            }

            // Separator row styling
            updateSeparator(showsList, day);

            // Button enhancements
            showsList.querySelectorAll('.status-info').forEach(btn =>
                btn.classList.add('btn')
            );

            showsList.querySelectorAll('.property').forEach(prop =>
                prop.style.setProperty('display', 'block')
            );
        });
    });
}

export default function initCinemaPlanner() {
    const root = document.querySelector('.cinema-planner');

    if (!root) {
        return;
    }

    const { isDragging } = bindStickyHeader(root);
    const { url, paramDays, now, startDate, endDate, hasDateRange } = resolveInitialState(root);

    if (!hasDateRange) {
        setTodayButtonValue(now);
    }

    const { dayFilters, allFilter } = renderDayFilters(startDate, endDate, root, hasDateRange);

    applyPresetDayFilters(paramDays, dayFilters, allFilter);

    function filterEvents(selectedDays) {
        const movies = document.querySelectorAll('.movie-card');

        movies.forEach(movie => {
            const eventDays = movie.querySelectorAll('.events .day');

            eventDays.forEach(day => {
                const showDate = day.dataset?.date ?? '';
                const visible = selectedDays.length === 0 || selectedDays.includes(showDate);
                day.style.display = visible ? '' : 'none';
            });

            const hasVisibleShows = Array.from(eventDays)
                .filter(day => day.style.display !== 'none')
                .some(day => day.querySelector('.no-show-placeholder')?.style.display !== 'block');

            movie.classList.toggle('hide-on-mobile', !hasVisibleShows);
        });

        toggleEmptyListVisibility();
    }

    const initiallySelected = getSelectedDays(dayFilters);

    updateMovies();
    filterEvents(initiallySelected);

    window.addEventListener('resize', debounce(() => {
        requestAnimationFrame(updateMovies);
    }, 150));

    bindFilterClickHandlers(dayFilters, allFilter, root, isDragging, (selected) => {
        filterEvents(selected);
        updateDaysInUrl(selected, url);
    });
}
